import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RetellSignatureCard = () => {
  const [state, setState] = useState(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/api-keys/retell`).then((r) => setState(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!state) return null;

  const save = async (apiKey) => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/api-keys/retell`, { api_key: apiKey });
      setState(data); setValue("");
      toast.success(apiKey ? "Retell key saved — voice endpoints now require a valid X-Retell-Signature" : "Retell key removed — signature check disabled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-white border-0 shadow-lg" data-testid="retell-signature-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-indigo-600" /> Retell signature check
          <Badge className={state.configured ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600"} data-testid="retell-signature-status">{state.configured ? `ENFORCED · key …${state.hint}` : "OFF"}</Badge>
        </CardTitle>
        <p className="text-sm text-slate-600">Retell signs every custom-function request with your Retell API key (<code>X-Retell-Signature</code>). Paste that key here and the <code>/voice/*</code> endpoints will reject any call that isn't signed by your agent (in addition to the X-API-Key). Leave blank to rely on the API key only. Find it in the Retell dashboard → API Keys.</p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <Label htmlFor="retell-key">Retell API key</Label>
            <Input id="retell-key" type="password" value={value} onChange={(e) => setValue(e.target.value)} placeholder={state.configured ? "•••••••• (saved — paste to replace)" : "key_xxxxxxxxxxxxxxxx"} autoComplete="off" data-testid="retell-key-input" />
          </div>
          <Button onClick={() => save(value.trim())} disabled={busy || !value.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="retell-key-save-button">{busy ? "Saving…" : "Save key"}</Button>
          {state.configured && <Button variant="outline" onClick={() => window.confirm("Disable Retell signature verification?") && save("")} disabled={busy} data-testid="retell-key-clear-button">Remove</Button>}
        </div>
      </CardContent>
    </Card>
  );
};
